/**
 * TYPED-CHAT-SHELL-1 — pure layout decisions for the typed interview.
 *
 * The conversation is a three-region chat (header / scrolling thread / pinned
 * composer). These helpers keep the window from becoming the scroller, keep the
 * newest Pip question fully readable above the input, grow the composer with
 * the teller's words, and sit the shell above the software keyboard.
 */

export const TYPED_SHELL = {
  mobileHeaderPx: 48,
  mobileNavPx: 64,
  keyboardThresholdPx: 80,
  composerMinPx: 48,
  composerMaxPx: 168,
  questionPaddingPx: 12,
  desktopSidebarPx: 280,
  desktopSidebarCompactPx: 200,
  compactMaxPx: 1024,
};

/**
 * Where the pinned shell sits inside the viewport.
 * When the keyboard is open it covers the bottom nav, so we use the keyboard
 * overlap as the bottom inset and do not also reserve the nav.
 */
export function desktopSidebarPx(innerWidth) {
  const width = Number(innerWidth);
  if (Number.isFinite(width) && width > 0 && width <= TYPED_SHELL.compactMaxPx) {
    return TYPED_SHELL.desktopSidebarCompactPx;
  }
  return TYPED_SHELL.desktopSidebarPx;
}

export function typedShellInsets({
  innerHeight,
  visualViewportHeight,
  visualViewportOffsetTop = 0,
  mobile = true,
  innerWidth,
  headerPx = TYPED_SHELL.mobileHeaderPx,
  navPx = TYPED_SHELL.mobileNavPx,
  safeAreaPx = 0,
  keyboardThresholdPx = TYPED_SHELL.keyboardThresholdPx,
} = {}) {
  const height = Number(visualViewportHeight);
  const win = Number(innerHeight);
  const offsetTop = Number(visualViewportOffsetTop) || 0;
  const left = mobile ? 0 : desktopSidebarPx(innerWidth);
  if (!Number.isFinite(win) || !Number.isFinite(height) || win <= 0 || height <= 0) {
    return {
      top: mobile ? headerPx : 0,
      bottom: mobile ? navPx + safeAreaPx : 0,
      left,
      keyboardOpen: false,
    };
  }
  const overlap = Math.max(0, win - height - offsetTop);
  const keyboardOpen = overlap >= keyboardThresholdPx;
  return {
    top: mobile ? headerPx : 0,
    bottom: keyboardOpen ? overlap : (mobile ? navPx + safeAreaPx : 0),
    left,
    keyboardOpen,
  };
}

/**
 * Scroll the thread so the newest Pip question is fully visible just above
 * the composer. A question taller than the pane is pinned at its START so
 * the first line is never clipped mid-glyph.
 */
export function nextThreadScrollTop({
  threadScrollTop = 0,
  threadClientHeight,
  threadScrollHeight,
  questionOffsetTop,
  questionHeight,
  padding = TYPED_SHELL.questionPaddingPx,
} = {}) {
  const pane = Number(threadClientHeight);
  const content = Number(threadScrollHeight);
  const qTop = Number(questionOffsetTop);
  const qH = Number(questionHeight);
  if (![pane, content, qTop, qH].every(Number.isFinite) || pane <= 0) {
    return { next: threadScrollTop, mode: 'none', delta: 0 };
  }
  const maxScroll = Math.max(0, content - pane);
  let next;
  let mode;
  if (qH + padding * 2 >= pane) {
    next = qTop - padding;
    mode = 'start';
  } else {
    next = qTop + qH + padding - pane;
    mode = 'end';
  }
  next = Math.max(0, Math.min(maxScroll, next));
  return { next, mode, delta: next - threadScrollTop };
}

export function applyThreadScroll(threadEl, questionEl, padding = TYPED_SHELL.questionPaddingPx) {
  if (!threadEl || !questionEl) return { scrolled: false, mode: 'none', delta: 0 };
  const threadBox = threadEl.getBoundingClientRect();
  const questionBox = questionEl.getBoundingClientRect();
  const questionOffsetTop = questionBox.top - threadBox.top + threadEl.scrollTop;
  const planned = nextThreadScrollTop({
    threadScrollTop: threadEl.scrollTop,
    threadClientHeight: threadEl.clientHeight,
    threadScrollHeight: threadEl.scrollHeight,
    questionOffsetTop,
    questionHeight: questionEl.offsetHeight || questionBox.height,
    padding,
  });
  if (planned.delta !== 0) threadEl.scrollTop = planned.next;
  return { scrolled: planned.delta !== 0, ...planned };
}

/** Grow the composer with the text. No inner scrollbar until the max height. */
export function autosizeComposer(el, { minPx = TYPED_SHELL.composerMinPx, maxPx = TYPED_SHELL.composerMaxPx } = {}) {
  if (!el) return { height: 0, clipped: false };
  el.style.height = 'auto';
  const raw = el.scrollHeight || minPx;
  const height = Math.min(Math.max(raw, minPx), maxPx);
  el.style.height = `${height}px`;
  el.style.overflowY = raw > maxPx ? 'auto' : 'hidden';
  return { height, clipped: raw > maxPx };
}

export function composerSendDisabled(value, { loading = false, transcribing = false } = {}) {
  return !String(value || '').trim() || !!loading || !!transcribing;
}

/**
 * VOICE-READY-SHELL-1 — pre-call voice chrome (Ready / Connecting / fail).
 * Live listening / waveform / stop-send are NOT covered here.
 */
export const VOICE_READY = {
  pipSlotPx: 120,
  connectingTimeoutMs: 15000,
};

/** Quiet hint before Tap to begin. Never prompts. Denied / missing mic only. */
export function classifyMicPreflight({
  hasGetUserMedia = false,
  permissionState = 'unknown',
  audioInputCount = null,
} = {}) {
  if (!hasGetUserMedia) {
    return {
      ok: false,
      reason: 'no-api',
      hint: 'This device cannot use the microphone. You can type your story instead.',
    };
  }
  if (permissionState === 'denied') {
    return {
      ok: false,
      reason: 'denied',
      hint: 'Microphone access is blocked. Allow it in your settings, or type instead.',
    };
  }
  // enumerateDevices often returns 0 inputs before permission. Only trust an
  // empty list after the browser has already granted access.
  if (audioInputCount === 0 && permissionState === 'granted') {
    return {
      ok: false,
      reason: 'no-device',
      hint: 'No microphone was found. Plug one in, or type instead.',
    };
  }
  return {
    ok: true,
    reason: permissionState === 'granted' ? 'granted' : 'unknown',
    hint: '',
  };
}

export function isVoiceConnectingTimeout({
  startedAt,
  now,
  timeoutMs = VOICE_READY.connectingTimeoutMs,
} = {}) {
  if (!Number.isFinite(startedAt) || !Number.isFinite(now) || !Number.isFinite(timeoutMs)) return false;
  return now - startedAt >= timeoutMs;
}

/** Pre-call frame: Ready, recoverable fail, or connecting. Not a live session. */
export function isVoicePreCallPhase(phase, { isConnecting = false } = {}) {
  if (phase === 'connected' || phase === 'done' || phase === 'writingup') return false;
  return phase === 'idle' || phase === 'error' || !!isConnecting;
}
